import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface ConfluenceInstallation {
  id: string;
  cloudId: string;
  siteUrl: string;
  organizationId: string;
  isActive: boolean;
  installedAt: string;
}

interface ConfluenceState {
  currentInstallation: ConfluenceInstallation | null;
  installations: ConfluenceInstallation[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: ConfluenceState = {
  currentInstallation: null,
  installations: [],
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getInstallations = createAsyncThunk(
  'confluence/getInstallations',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/confluence/installations?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getInstallation = createAsyncThunk(
  'confluence/getInstallation',
  async (installationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/confluence/installations/${installationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const disconnectConfluence = createAsyncThunk(
  'confluence/disconnectConfluence',
  async (installationId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/confluence/installations/${installationId}`, { method: 'DELETE' });
      return installationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get installations
  [getInstallations.pending.type]: { status: 'loading' as const },
  [getInstallations.fulfilled.type]: (state: ConfluenceState, action: any) => {
    // Handle null response (404 - no installations found)
    if (!action.payload) {
      state.installations = [];
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response: { response: [...], metadata: {...} }
    // Backend already filters by isActive: true
    const installations = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.installations || action.payload.installations || [];

    state.installations = installations;
    state.currentInstallation = installations[0] || null;
    state.status = 'success';
    state.error = '';
  },
  [getInstallations.rejected.type]: (state: ConfluenceState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installations';
  },

  // Get single installation
  [getInstallation.pending.type]: { status: 'loading' as const },
  [getInstallation.fulfilled.type]: (state: ConfluenceState, action: any) => {
    // Handle null response (404 - installation not found)
    if (!action.payload) {
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend wraps response in { response: {...}, metadata: {...} }
    state.currentInstallation = action.payload.response || action.payload;
    state.status = 'success';
    state.error = '';
  },
  [getInstallation.rejected.type]: (state: ConfluenceState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installation';
  },

  // Disconnect Confluence
  [disconnectConfluence.pending.type]: { status: 'loading' as const },
  [disconnectConfluence.fulfilled.type]: (state: ConfluenceState) => {
    state.currentInstallation = null;
    state.installations = [];
    state.status = 'success';
    state.error = '';
  },
  [disconnectConfluence.rejected.type]: (state: ConfluenceState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to disconnect Confluence';
  },
};

// ==============================|| SLICE ||============================== //

export const confluenceSlice = createSlice({
  name: 'confluence',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentInstallation(state, action) {
      state.currentInstallation = action.payload;
    },
    clearConfluenceState(state) {
      state.currentInstallation = null;
      state.installations = [];
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentInstallation, clearConfluenceState } = confluenceSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectConfluenceInstallations = (state: any) => state.confluence.installations;
export const selectCurrentInstallation = (state: any) => state.confluence.currentInstallation;
export const selectConfluenceStatus = (state: any) => state.confluence.status;
export const selectConfluenceError = (state: any) => state.confluence.error;

// ==============================|| HELPER ||============================== //

export const initiateConfluenceConnect = createAsyncThunk(
  'confluence/initiateConnect',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      // Call API with authentication to get Confluence installation URL (same as GitHub/GitLab)
      const response = await apiRequest(`/confluence/install?organizationId=${organizationId}`);
      const installUrl = response.response?.installUrl || response.installUrl;

      if (!installUrl) {
        throw new Error('No installation URL returned from API');
      }

      // Redirect browser to Atlassian OAuth
      window.location.href = installUrl;

      return { installUrl };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export default confluenceSlice.reducer;
